import {
	ArrowUpFromDot,
	Calendar,
	CheckCircle,
	ChevronLeft,
	Clock,
	Copy,
	FileText,
	Globe,
	MapPin,
	Plus,
	RotateCcw,
	User,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Toaster, toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';
import {
	Sheet,
	SheetContent,
	SheetHeader,
	SheetTitle,
} from '@/components/ui/sheet';
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import {
	type Alert,
	type DispositionDecision,
	generateMatchesForAlert,
	getAlertsForCase,
	getCaseById,
	getEntityForAlert,
	getTimelineForCase,
	MOCK_USERS,
} from '@/data/aml/mockData';
import { cn } from '@/lib/utils';
import { Routes } from '@/routes/routes';

// ── Helpers ───────────────────────────────────────────────────────────────────

function dispositionLabel(d: DispositionDecision | string | undefined): string {
	if (!d) return '—';
	return String(d)
		.replace(/_/g, ' ')
		.replace(/\b\w/g, (l) => l.toUpperCase());
}

function dispositionChipClass(
	d: DispositionDecision | string | undefined,
): string {
	const v = String(d || '').toLowerCase();
	if (v.includes('true')) return 'bg-red-50 text-red-700';
	if (v.includes('false')) return 'bg-green-50 text-green-700';
	if (v.includes('potential')) return 'bg-orange-50 text-orange-700';
	return 'bg-[#f0f0f0] text-[#646464]';
}

function scoreColor(level: string): string {
	const v = level.toLowerCase();
	if (v === 'very_high' || v === 'high') return 'text-red-700 bg-red-50';
	if (v === 'medium') return 'text-orange-700 bg-orange-50';
	return 'text-green-700 bg-green-50';
}

function moduleBadge(name: string): string {
	if (name === 'transaction_monitoring') return 'TM';
	if (name === 'name_screening') return 'NS';
	return '?';
}

function priorityClass(p: string): string {
	const v = p.toLowerCase();
	if (v.includes('very') || v === 'high') return 'bg-red-50 text-red-700';
	if (v === 'medium') return 'bg-yellow-50 text-yellow-700';
	return 'bg-green-50 text-green-700';
}

function formatDate(iso: string): string {
	return new Date(iso).toLocaleDateString('en-US', {
		year: 'numeric',
		month: 'short',
		day: 'numeric',
	});
}

// ── Alert Tab ──────────────────────────────────────────────────────────────────

function AlertContent({ alert }: { alert: Alert }) {
	const entity = getEntityForAlert(alert.id);
	const matches = useMemo(() => generateMatchesForAlert(alert.id), [alert.id]);
	const [selectedMatchId, setSelectedMatchId] = useState<string | null>(
		matches[0]?.id ?? null,
	);

	const selectedMatch = matches.find((m) => m.id === selectedMatchId) ?? null;
	const isNS = alert.module_name === 'name_screening';

	return (
		<div className="space-y-4 p-4">
			{/* Customer Profile */}
			{entity ? (
				<Card className="border border-[#E5E5E5]">
					<CardContent className="p-4">
						<div className="mb-3 text-sm font-semibold text-[#0A0A0A]">
							Customer Profile
						</div>
						<div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
							<ProfileField
								icon={User}
								label="Name"
								value={entity.customer_name}
							/>
							<ProfileField icon={User} label="ID" value={entity.customer_id} />
							<ProfileField
								icon={User}
								label="Type"
								value={entity.customer_type}
							/>
							<ProfileField icon={User} label="Gender" value={entity.gender} />
							<ProfileField
								icon={Calendar}
								label="Date of Birth"
								value={entity.date_of_birth}
							/>
							<ProfileField
								icon={Globe}
								label="Nationality"
								value={entity.nationality}
							/>
							<ProfileField
								icon={Globe}
								label="Country"
								value={entity.country}
							/>
							<ProfileField
								icon={MapPin}
								label="Address"
								value={entity.address}
							/>
							<div className="flex flex-col gap-1">
								<span className="text-xs text-[#737373]">PEP Status</span>
								<Badge
									variant={
										entity.pep_status === 'PEP' ? 'destructive' : 'secondary'
									}
									className="w-fit text-xs"
								>
									{entity.pep_status}
								</Badge>
							</div>
						</div>
					</CardContent>
				</Card>
			) : (
				<div className="rounded-xl border border-dashed border-[#D4D4D4] px-6 py-8 text-center text-sm text-[#737373]">
					Customer profile unavailable.
				</div>
			)}

			{/* Matches — only for NS */}
			{isNS && (
				<>
					<Card className="border border-[#E5E5E5]">
						<CardContent className="p-4">
							<div className="mb-3 text-sm font-semibold text-[#0A0A0A]">
								Watchlist Matches ({matches.length})
							</div>
							{matches.length === 0 ? (
								<p className="text-sm text-[#737373]">No matches found.</p>
							) : (
								<div className="rounded-md border border-[#E5E5E5] overflow-hidden">
									<Table>
										<TableHeader>
											<TableRow className="bg-[#FAFAFA]">
												<TableHead className="text-xs font-semibold text-[#737373]">
													Name
												</TableHead>
												<TableHead className="text-xs font-semibold text-[#737373]">
													Score
												</TableHead>
												<TableHead className="text-xs font-semibold text-[#737373]">
													Category
												</TableHead>
												<TableHead className="text-xs font-semibold text-[#737373]">
													Nationality
												</TableHead>
												<TableHead className="text-xs font-semibold text-[#737373]">
													Source
												</TableHead>
											</TableRow>
										</TableHeader>
										<TableBody>
											{matches.map((m) => (
												<TableRow
													key={m.id}
													className={cn(
														'cursor-pointer transition-colors',
														selectedMatchId === m.id
															? 'bg-blue-50'
															: 'hover:bg-[#FAFAFA]',
													)}
													onClick={() =>
														setSelectedMatchId(
															m.id === selectedMatchId ? null : m.id,
														)
													}
												>
													<TableCell className="text-sm font-medium text-[#0A0A0A]">
														{m.name}
													</TableCell>
													<TableCell>
														<span
															className={cn(
																'inline-flex items-center rounded-md px-2 py-0.5 text-xs font-semibold',
																scoreColor(m.match_level),
															)}
														>
															{m.score}%
														</span>
													</TableCell>
													<TableCell>
														<span className="text-xs text-[#525252]">
															{m.category}
														</span>
													</TableCell>
													<TableCell>
														<span className="text-xs text-[#525252]">
															{m.nationality}
														</span>
													</TableCell>
													<TableCell>
														<span className="text-xs text-[#525252]">
															{m.source}
														</span>
													</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
								</div>
							)}
						</CardContent>
					</Card>

					{selectedMatch && (
						<Card className="border border-[#E5E5E5]">
							<CardContent className="p-4">
								<div className="mb-3 text-sm font-semibold text-[#0A0A0A]">
									Match Detail — {selectedMatch.name}
								</div>
								<div className="grid grid-cols-2 gap-3 md:grid-cols-3">
									<ProfileField
										icon={User}
										label="Name"
										value={selectedMatch.name}
									/>
									<ProfileField
										icon={Calendar}
										label="Date of Birth"
										value={selectedMatch.dob}
									/>
									<ProfileField
										icon={Globe}
										label="Nationality"
										value={selectedMatch.nationality}
									/>
									<ProfileField
										icon={User}
										label="Category"
										value={selectedMatch.category}
									/>
									<ProfileField
										icon={User}
										label="PEP Status"
										value={selectedMatch.pep_status}
									/>
									<ProfileField
										icon={User}
										label="Source"
										value={selectedMatch.source}
									/>
								</div>
								{selectedMatch.aliases.length > 0 && (
									<div className="mt-3">
										<div className="text-xs text-[#737373] mb-1">Aliases</div>
										<div className="flex flex-wrap gap-1">
											{selectedMatch.aliases.map((a) => (
												<span
													key={a}
													className="inline-flex items-center rounded-full bg-[#F5F5F5] px-2.5 py-0.5 text-xs text-[#525252]"
												>
													{a}
												</span>
											))}
										</div>
									</div>
								)}
							</CardContent>
						</Card>
					)}
				</>
			)}

			{/* TM placeholder */}
			{!isNS && (
				<Card className="border border-[#E5E5E5]">
					<CardContent className="p-8 text-center text-sm text-[#737373]">
						Transaction monitoring details will be shown here.
					</CardContent>
				</Card>
			)}
		</div>
	);
}

function ProfileField({
	icon: _Icon,
	label,
	value,
}: {
	icon: React.ElementType;
	label: string;
	value: string;
}) {
	return (
		<div className="flex flex-col gap-1">
			<span className="text-xs text-[#737373]">{label}</span>
			<span className="text-sm font-medium text-[#0A0A0A] truncate">
				{value || '—'}
			</span>
		</div>
	);
}

// ── Timeline Sheet ─────────────────────────────────────────────────────────────

function TimelineSheet({
	open,
	onOpenChange,
	caseId,
}: {
	open: boolean;
	onOpenChange: (v: boolean) => void;
	caseId: string;
}) {
	const events = useMemo(() => getTimelineForCase(caseId), [caseId]);

	return (
		<Sheet open={open} onOpenChange={onOpenChange}>
			<SheetContent className="w-[420px] sm:w-[480px] overflow-y-auto">
				<SheetHeader className="mb-4">
					<SheetTitle>Timeline & Notes</SheetTitle>
				</SheetHeader>
				<div className="space-y-3">
					{events.map((evt, i) => (
						<div key={evt.id} className="flex gap-3">
							<div className="flex flex-col items-center">
								<div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#F5F5F5] text-[#737373]">
									{i === 0 ? (
										<CheckCircle className="h-4 w-4" />
									) : (
										<Clock className="h-3.5 w-3.5" />
									)}
								</div>
								{i < events.length - 1 && (
									<div className="mt-1 w-px flex-1 bg-[#E5E5E5]" />
								)}
							</div>
							<div className="pb-4">
								<div className="text-sm font-medium text-[#0A0A0A]">
									{evt.description}
								</div>
								<div className="text-xs text-[#737373] mt-0.5">
									{evt.actor} · {formatDate(evt.timestamp)}
								</div>
							</div>
						</div>
					))}
				</div>
			</SheetContent>
		</Sheet>
	);
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function CaseDetailPage() {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const caseData = useMemo(() => getCaseById(id ?? ''), [id]);
	const alerts = useMemo(() => getAlertsForCase(id ?? ''), [id]);

	const [selectedAlertId, setSelectedAlertId] = useState<string | null>(null);
	const [timelineOpen, setTimelineOpen] = useState(false);
	const [disposeOpen, setDisposeOpen] = useState(false);
	const [assignDialogOpen, setAssignDialogOpen] = useState(false);
	const [escalateOpen, setEscalateOpen] = useState(false);
	const [reopenOpen, setReopenOpen] = useState(false);
	const [strOpen, setStrOpen] = useState(false);
	const [reopenReason, setReopenReason] = useState('');
	const [selectedReopenAlerts, setSelectedReopenAlerts] = useState<string[]>(
		[],
	);
	const [disposeDecision, setDisposeDecision] = useState('');
	const [optimisticStatus, setOptimisticStatus] = useState<string | null>(null);
	const [optimisticDisposition, setOptimisticDisposition] =
		useState<DispositionDecision>(null);
	const [selectedAssignee, setSelectedAssignee] = useState('');
	const [escalateReason, setEscalateReason] = useState('');

	const selectedAlert = useMemo(
		() => alerts.find((a) => a.id === selectedAlertId) ?? alerts[0] ?? null,
		[alerts, selectedAlertId],
	);

	const effectiveStatus = optimisticStatus ?? caseData?.status ?? '';
	const effectiveDisposition =
		optimisticDisposition ?? caseData?.disposition_decision ?? null;
	const isClosed = effectiveStatus === 'closed';
	const isLocked = isClosed || !!effectiveDisposition;

	if (!caseData) {
		return (
			<div className="min-h-screen bg-background p-8">
				<p className="text-sm text-[#737373]">Case not found.</p>
			</div>
		);
	}

	const handleCloseCase = () => {
		if (!disposeDecision) {
			toast.error('Select a disposition');
			return;
		}
		setOptimisticDisposition(disposeDecision as DispositionDecision);
		setOptimisticStatus('closed');
		setDisposeOpen(false);
		toast.success('Case closed successfully');
	};

	const handleReopen = () => {
		if (!reopenReason.trim()) {
			toast.error('Enter reopen reason');
			return;
		}
		if (!selectedReopenAlerts.length) {
			toast.error('Select at least one alert');
			return;
		}
		setOptimisticStatus('reopened');
		setOptimisticDisposition(null);
		setReopenOpen(false);
		setReopenReason('');
		toast.success('Case reopened successfully');
	};

	const handleEscalate = () => {
		if (!escalateReason.trim()) {
			toast.error('Enter escalation reason');
			return;
		}
		setOptimisticStatus('escalated');
		setEscalateOpen(false);
		setEscalateReason('');
		toast.success('Case escalated');
	};

	return (
		<div className="flex min-h-screen flex-col bg-background">
			<Toaster richColors position="top-right" />

			{/* Header */}
			<div className="border-b border-border bg-white px-6 py-3">
				<div className="flex flex-wrap items-center gap-3">
					<button
						type="button"
						onClick={() => navigate(Routes.AML_CASE_MANAGER)}
						className="flex items-center gap-1 text-sm text-[#0070C9] hover:underline"
					>
						<ChevronLeft className="h-4 w-4" /> Case Manager
					</button>
					<span className="text-[#A3A3A3]">›</span>
					<span className="text-sm font-medium text-[#0A0A0A]">
						{caseData.code}
					</span>
				</div>
				<div className="mt-2 flex flex-wrap items-center justify-between gap-3">
					<div className="flex flex-wrap items-center gap-2">
						<h1 className="text-lg font-semibold text-[#0A0A0A]">
							Case Details
						</h1>
						<button
							type="button"
							onClick={() => {
								navigator.clipboard.writeText(caseData.code);
							}}
							className="inline-flex items-center gap-1 rounded-md bg-[#F0F0F0] px-1.5 py-0.5 text-xs font-medium text-[#646464] hover:bg-[#E5E5E5]"
						>
							{caseData.code} <Copy className="h-3 w-3" />
						</button>
						<span className="inline-flex items-center rounded-md border border-[#E5E5E5] bg-white px-1.5 py-0.5 text-xs font-medium text-[#0A0A0A]">
							{caseData.case_age} Days
						</span>
						<span className="inline-flex items-center rounded-md border border-[#E5E5E5] bg-white px-1.5 py-0.5 text-xs font-medium text-[#0A0A0A]">
							{alerts.length} Alerts
						</span>
					</div>
					<Button
						variant="outline"
						size="sm"
						className="gap-1.5"
						onClick={() => setTimelineOpen(true)}
					>
						<Clock className="h-4 w-4" /> Timeline & Notes
					</Button>
				</div>
			</div>

			{/* Action Bar */}
			<Card className="mx-6 mt-4 border border-[#E5E5E5]">
				<CardContent className="p-4">
					<div className="flex flex-wrap items-center gap-6">
						{/* Status */}
						<div className="flex items-center gap-2 border-r border-[#E5E5E5] pr-6">
							<span className="text-sm font-medium text-[#737373]">Status</span>
							<span
								className={cn(
									'inline-flex items-center rounded-md border px-2 py-0.5 text-sm font-medium',
									effectiveStatus === 'open'
										? 'bg-blue-50 text-blue-700 border-blue-200'
										: effectiveStatus === 'closed'
											? 'bg-gray-100 text-gray-700 border-gray-200'
											: effectiveStatus === 'escalated'
												? 'bg-orange-50 text-orange-700 border-orange-200'
												: effectiveStatus === 'pending'
													? 'bg-yellow-50 text-yellow-700 border-yellow-200'
													: 'bg-purple-50 text-purple-700 border-purple-200',
								)}
							>
								{effectiveStatus === 'pending'
									? 'Pending Review'
									: effectiveStatus.charAt(0).toUpperCase() +
										effectiveStatus.slice(1)}
							</span>
						</div>

						{/* Assigned To */}
						<div className="flex items-center gap-2 border-r border-[#E5E5E5] pr-6">
							<span className="text-sm font-medium text-[#737373]">
								Assigned To
							</span>
							{!isLocked ? (
								<Select
									value={
										selectedAssignee || caseData.assignee?.id || 'unassigned'
									}
									onValueChange={(v) => {
										if (v !== 'unassigned') {
											setSelectedAssignee(v);
											setAssignDialogOpen(true);
										}
									}}
								>
									<SelectTrigger className="h-8 min-w-[150px] border-[#E5E5E5] text-sm text-[#737373]">
										<SelectValue placeholder="Unassigned" />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="unassigned">Unassigned</SelectItem>
										{MOCK_USERS.map((u) => (
											<SelectItem key={u.id} value={u.id}>
												{u.name}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							) : (
								<span className="text-sm text-[#525252]">
									{caseData.assignee?.name ?? 'Unassigned'}
								</span>
							)}
						</div>

						{/* Disposition */}
						<div className="flex items-center gap-2 border-r border-[#E5E5E5] pr-6">
							<span className="text-sm font-medium text-[#737373]">
								Disposition
							</span>
							{isLocked ? (
								<span
									className={cn(
										'inline-flex items-center rounded-md px-2 py-0.5 text-sm font-medium',
										dispositionChipClass(effectiveDisposition),
									)}
								>
									{dispositionLabel(effectiveDisposition || effectiveStatus)}
								</span>
							) : (
								<Select
									value=""
									onValueChange={(v) => {
										setDisposeDecision(v);
										setDisposeOpen(true);
									}}
								>
									<SelectTrigger className="h-8 min-w-[160px] border-[#E5E5E5] text-sm text-[#737373]">
										<SelectValue placeholder="Select Disposition" />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="true_match" className="text-red-600">
											True Match
										</SelectItem>
										<SelectItem value="false_match" className="text-green-600">
											False Match
										</SelectItem>
										<SelectItem
											value="potential_match"
											className="text-orange-600"
										>
											Potential Match
										</SelectItem>
									</SelectContent>
								</Select>
							)}
						</div>

						{/* Actions */}
						<div className="ml-auto flex items-center gap-2">
							{!isLocked && (
								<>
									<Button
										variant="outline"
										size="sm"
										className="gap-1.5"
										onClick={() => toast.info('Add alerts dialog')}
									>
										<Plus className="h-4 w-4" /> Add Alerts
									</Button>
									<Button
										variant="outline"
										size="sm"
										className="gap-1.5 border-orange-300 bg-orange-50 text-orange-600 hover:bg-orange-100 hover:text-orange-600"
										onClick={() => setEscalateOpen(true)}
									>
										<ArrowUpFromDot className="h-4 w-4" /> Escalate
									</Button>
								</>
							)}
							{isClosed && (
								<>
									<Button
										size="sm"
										className="gap-1.5 bg-emerald-600 text-white hover:bg-emerald-700"
										onClick={() => setStrOpen(true)}
									>
										<FileText className="h-4 w-4" />
										{caseData.str_report_id ? 'Open STR' : 'File STR'}
									</Button>
									<Button
										variant="outline"
										size="sm"
										className="gap-1.5 border-purple-300 bg-purple-50 text-purple-700 hover:bg-purple-100 hover:text-purple-700"
										onClick={() => {
											setSelectedReopenAlerts(alerts.map((a) => a.id));
											setReopenOpen(true);
										}}
									>
										<RotateCcw className="h-4 w-4" /> Reopen
									</Button>
								</>
							)}
						</div>
					</div>
				</CardContent>
			</Card>

			{/* Alert Tabs */}
			<div className="flex-1 px-6 pb-6 mt-4">
				{alerts.length === 0 ? (
					<div className="rounded-xl border border-dashed border-[#D4D4D4] p-10 text-center text-sm text-[#737373]">
						No alerts linked to this case.
					</div>
				) : (
					<>
						<div className="flex items-end gap-0 border-b border-[#E5E5E5] overflow-x-auto pb-0">
							{alerts.map((alert) => {
								const isActive =
									(selectedAlertId ?? alerts[0]?.id) === alert.id;
								return (
									<button
										key={alert.id}
										type="button"
										onClick={() => setSelectedAlertId(alert.id)}
										className={cn(
											'relative flex min-w-[280px] items-center gap-2 rounded-t-xl border border-b-0 border-[#E5E5E5] bg-white px-3 pt-4 pb-3 text-left shadow-sm transition-colors',
											isActive
												? 'z-10 border-t-[3px] border-t-[#0d74ce]'
												: 'hover:bg-[#FAFAFA]',
										)}
									>
										<span className="inline-flex shrink-0 items-center rounded-md bg-[#F0F0F0] px-1.5 py-0.5 text-xs font-medium text-[#646464]">
											{moduleBadge(alert.module_name)}
										</span>
										<span className="text-xs font-medium text-[#0A0A0A]">
											{alert.code}
										</span>
										{alert.disposition_decision && (
											<span
												className={cn(
													'inline-flex items-center rounded-md px-1.5 py-0.5 text-xs font-medium',
													dispositionChipClass(alert.disposition_decision),
												)}
											>
												{dispositionLabel(alert.disposition_decision)}
											</span>
										)}
										<span
											className={cn(
												'ml-auto inline-flex shrink-0 items-center rounded-md px-1.5 py-0.5 text-xs font-medium',
												priorityClass(alert.priority),
											)}
										>
											{alert.priority.toUpperCase().replace('_', ' ')}
										</span>
									</button>
								);
							})}
						</div>
						{selectedAlert && <AlertContent alert={selectedAlert} />}
					</>
				)}
			</div>

			{/* Timeline Sheet */}
			<TimelineSheet
				open={timelineOpen}
				onOpenChange={setTimelineOpen}
				caseId={id ?? ''}
			/>

			{/* Close / Dispose Dialog */}
			<Dialog
				open={disposeOpen}
				onOpenChange={(v) => {
					setDisposeOpen(v);
					if (!v) setDisposeDecision('');
				}}
			>
				<DialogContent className="max-w-[480px]">
					<DialogHeader>
						<DialogTitle>Close Case</DialogTitle>
						<DialogDescription>
							Confirm the disposition decision to close this case.
						</DialogDescription>
					</DialogHeader>
					<div className="space-y-3 py-2">
						<div className="text-sm text-[#737373]">Selected disposition:</div>
						<span
							className={cn(
								'inline-flex items-center rounded-md px-3 py-1 text-sm font-medium',
								dispositionChipClass(disposeDecision as DispositionDecision),
							)}
						>
							{dispositionLabel(disposeDecision as DispositionDecision)}
						</span>
					</div>
					<DialogFooter>
						<Button variant="outline" onClick={() => setDisposeOpen(false)}>
							Cancel
						</Button>
						<Button
							className="bg-black text-white hover:bg-black/90"
							onClick={handleCloseCase}
						>
							Confirm & Close
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Reopen Dialog */}
			<Dialog open={reopenOpen} onOpenChange={setReopenOpen}>
				<DialogContent className="max-w-[560px]">
					<DialogHeader>
						<DialogTitle>Reopen Case</DialogTitle>
						<DialogDescription>
							Select alerts to reopen with this case and add a reopen reason.
						</DialogDescription>
					</DialogHeader>
					<div className="space-y-4">
						<div>
							<div className="mb-2 text-sm font-medium text-[#0A0A0A]">
								Alerts
							</div>
							<div className="max-h-[200px] overflow-y-auto rounded-md border border-[#E5E5E5]">
								{alerts.map((alert) => (
									<div
										key={alert.id}
										className="flex items-center justify-between gap-3 border-b border-[#F5F5F5] px-4 py-3 last:border-b-0"
									>
										<div className="flex items-center gap-3">
											<Checkbox
												checked={selectedReopenAlerts.includes(alert.id)}
												onCheckedChange={() =>
													setSelectedReopenAlerts((prev) =>
														prev.includes(alert.id)
															? prev.filter((x) => x !== alert.id)
															: [...prev, alert.id],
													)
												}
											/>
											<div>
												<div className="text-sm font-semibold text-[#0A0A0A]">
													{alert.code}
												</div>
												<div className="text-xs text-[#737373]">
													{alert.entity_name}
												</div>
											</div>
										</div>
										<span
											className={cn(
												'inline-flex items-center rounded-md px-2 py-0.5 text-xs font-semibold uppercase',
												scoreColor(alert.score.level),
											)}
										>
											{alert.score.level.replace('_', ' ')}
										</span>
									</div>
								))}
							</div>
						</div>
						<div>
							<div className="mb-2 text-sm font-medium text-[#0A0A0A]">
								Reopen Reason <span className="text-red-500">*</span>
							</div>
							<Textarea
								value={reopenReason}
								onChange={(e) => setReopenReason(e.target.value)}
								placeholder="Enter reason for reopening this case..."
								className="min-h-[80px] resize-none border-[#E5E5E5] text-sm"
							/>
						</div>
					</div>
					<DialogFooter>
						<Button variant="outline" onClick={() => setReopenOpen(false)}>
							Cancel
						</Button>
						<Button
							className="bg-black text-white hover:bg-black/90"
							disabled={!reopenReason.trim() || !selectedReopenAlerts.length}
							onClick={handleReopen}
						>
							Reopen Case
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Escalate Dialog */}
			<Dialog open={escalateOpen} onOpenChange={setEscalateOpen}>
				<DialogContent className="max-w-[480px]">
					<DialogHeader>
						<DialogTitle>Escalate Case</DialogTitle>
						<DialogDescription>
							Escalate this case for urgent review.
						</DialogDescription>
					</DialogHeader>
					<div className="space-y-3">
						<div className="text-sm font-medium text-[#0A0A0A]">
							Reason <span className="text-red-500">*</span>
						</div>
						<Textarea
							value={escalateReason}
							onChange={(e) => setEscalateReason(e.target.value)}
							placeholder="Enter escalation reason..."
							className="min-h-[80px] resize-none"
						/>
					</div>
					<DialogFooter>
						<Button variant="outline" onClick={() => setEscalateOpen(false)}>
							Cancel
						</Button>
						<Button
							className="bg-orange-600 text-white hover:bg-orange-700"
							disabled={!escalateReason.trim()}
							onClick={handleEscalate}
						>
							Escalate
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* STR Dialog */}
			<Dialog open={strOpen} onOpenChange={setStrOpen}>
				<DialogContent className="max-w-[480px]">
					<DialogHeader>
						<DialogTitle>
							{caseData.str_report_id ? 'View STR Report' : 'File STR Report'}
						</DialogTitle>
					</DialogHeader>
					<div className="space-y-3 py-2">
						{caseData.str_report_id ? (
							<p className="text-sm text-[#525252]">
								STR ID:{' '}
								<span className="font-semibold">{caseData.str_report_id}</span>
							</p>
						) : (
							<p className="text-sm text-[#737373]">
								File a Suspicious Transaction Report for case {caseData.code}.
							</p>
						)}
					</div>
					<DialogFooter>
						<Button variant="outline" onClick={() => setStrOpen(false)}>
							Close
						</Button>
						{!caseData.str_report_id && (
							<Button
								className="bg-emerald-600 text-white hover:bg-emerald-700"
								onClick={() => {
									toast.success('STR filed successfully');
									setStrOpen(false);
								}}
							>
								Submit STR
							</Button>
						)}
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Assign Dialog */}
			<Dialog
				open={assignDialogOpen}
				onOpenChange={(v) => {
					setAssignDialogOpen(v);
					if (!v) setSelectedAssignee('');
				}}
			>
				<DialogContent className="max-w-[480px]">
					<DialogHeader>
						<DialogTitle>Assign Case</DialogTitle>
					</DialogHeader>
					<p className="text-sm text-[#737373]">
						Assign to{' '}
						{MOCK_USERS.find((u) => u.id === selectedAssignee)?.name ??
							selectedAssignee}
						?
					</p>
					<DialogFooter>
						<Button
							variant="outline"
							onClick={() => setAssignDialogOpen(false)}
						>
							Cancel
						</Button>
						<Button
							className="bg-black text-white hover:bg-black/90"
							onClick={() => {
								toast.success('Case assigned');
								setAssignDialogOpen(false);
							}}
						>
							Confirm
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
}
